import type React from "react"

import type { AppShellNavigationItem } from "@/app-shell/navigation/AppShellContributionsContext"
import { Settings } from "lucide-react"

import { openCoviHome } from "@/lib/coviEvents"
import { filterNavigationItems } from "@/utils/auth/user-access"

import {
  HOME_BASE_HREF,
  siteNavigationConfig,
  SOFIA_HOME_NAV_OVERRIDE,
} from "../config/siteNavigation"
import type { NavigationFeatureFlag } from "../types"
import { DIRECT_SHORTCUTS_BY_BASE_HREF } from "../utils/navigationCommandTree"
import {
  getSidebarItemKey,
  getSidebarSubItemKey,
} from "../utils/sidebarNavKeys"

const SETTINGS_KEY = "settings"
const SETTINGS_TITLE = "Settings"
const SETTINGS_BASE_HREF = "/settings"
const HUMAN_REVIEW_LEGACY_BASE_HREF = "/review/projects"
const HUMAN_REVIEW_ENTRY_BASE_HREF = "/review"

interface NavigationFilterItem extends AppShellNavigationItem {
  [key: string]: unknown
  subItems?: NavigationFilterItem[]
}

/**
 * Sofia answers the event when she is mounted, and only then is the link's own
 * navigation suppressed; without a provider the plain `/home` href still works.
 * Modified and non-primary clicks are left alone so "open in new tab" behaves.
 */
function resumeSofiaOnHome(event: React.MouseEvent<HTMLAnchorElement>): void {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return
  }
  if (openCoviHome()) {
    event.preventDefault()
  }
}

interface BuildAppShellNavigationItemsOptions {
  featureFlagStatus: Partial<Record<NavigationFeatureFlag, boolean>>
  getHref: (baseHref: string) => string
  /** Whether the Human Review row may offer its create-project shortcut. */
  canCreateHumanReviewProject?: boolean
  userEmail: string
}

/**
 * With role modes on, only organization admins manage review projects; every
 * other member lands on their assignments. While the flag is unresolved the
 * destination can't tell which of the two applies, so the shortcut stays hidden.
 */
export function resolveCanCreateHumanReviewProject({
  isOrganizationAdmin,
  roleModesEnabled,
}: {
  isOrganizationAdmin: boolean
  roleModesEnabled: boolean | undefined
}): boolean {
  return roleModesEnabled === undefined
    ? false
    : !roleModesEnabled || isOrganizationAdmin
}

export function buildAppShellNavigationItems({
  featureFlagStatus,
  getHref,
  canCreateHumanReviewProject = true,
  userEmail,
}: BuildAppShellNavigationItemsOptions): AppShellNavigationItem[] {
  const getFeatureAwareBaseHref = (baseHref: string): string =>
    baseHref === HUMAN_REVIEW_LEGACY_BASE_HREF &&
    featureFlagStatus["human-review-role-modes"]
      ? HUMAN_REVIEW_ENTRY_BASE_HREF
      : baseHref
  const items: NavigationFilterItem[] = siteNavigationConfig.navItems
    .filter((item) => {
      if (!item.featureFlag) {
        return true
      }

      return featureFlagStatus[item.featureFlag] === true
    })
    .map((item): NavigationFilterItem => {
      const subItems = item.subItems
        ?.filter((subItem) => {
          if (!subItem.featureFlag) {
            return true
          }

          return featureFlagStatus[subItem.featureFlag] === true
        })
        .map((subItem) => {
          const baseHref = getFeatureAwareBaseHref(subItem.base_href)

          return {
            key: getSidebarSubItemKey(subItem),
            href: getHref(baseHref),
            base_href: baseHref,
            createHref:
              subItem.createBaseHref &&
              (subItem.base_href !== HUMAN_REVIEW_LEGACY_BASE_HREF ||
                canCreateHumanReviewProject)
                ? getHref(subItem.createBaseHref)
                : undefined,
            createLabel: subItem.createLabel,
            title: subItem.label,
            icon: subItem.icon,
            shortcutKey: DIRECT_SHORTCUTS_BY_BASE_HREF[baseHref],
          }
        })

      // Home is a Sofia surface: it wears the Sofia name and the Coval mark.
      const isSofiaHome = item.base_href === HOME_BASE_HREF
      const baseHref = getFeatureAwareBaseHref(item.base_href)
      const sidebarItem: NavigationFilterItem = {
        key: getSidebarItemKey(item),
        href: getHref(baseHref),
        base_href: baseHref,
        createHref: item.createBaseHref
          ? getHref(item.createBaseHref)
          : undefined,
        createLabel: item.createLabel,
        icon: isSofiaHome ? SOFIA_HOME_NAV_OVERRIDE.icon : item.icon,
        title: isSofiaHome ? SOFIA_HOME_NAV_OVERRIDE.label : item.label,
        onClick: isSofiaHome ? resumeSofiaOnHome : undefined,
        // Sofia's own pages hang under this entry as their own sidebar rows,
        // so it is selected on Home itself rather than on the whole section.
        matchExact: isSofiaHome || undefined,
        // A section heading is not a destination, so only an entry that is a
        // row in its own right carries a chord — the same rule the command
        // palette applies when it registers them.
        shortcutKey:
          subItems && subItems.length > 0
            ? undefined
            : DIRECT_SHORTCUTS_BY_BASE_HREF[baseHref],
        subItems: subItems && subItems.length > 0 ? subItems : undefined,
      }

      return sidebarItem
    })

  items.push({
    key: SETTINGS_KEY,
    href: getHref(SETTINGS_BASE_HREF),
    base_href: SETTINGS_BASE_HREF,
    title: SETTINGS_TITLE,
    icon: Settings,
    shortcutKey: DIRECT_SHORTCUTS_BY_BASE_HREF[SETTINGS_BASE_HREF],
  })

  return filterNavigationItems(items, userEmail)
}
